import {
  Anchor,
  Button,
  Card,
  Center,
  Divider,
  Stack,
  ThemeIcon,
  Title,
  TypographyStylesProvider,
  Image,
} from "@mantine/core";

import React from "react";
import DocsHighlightCard from "../articlesHelpers/DocsHighlightCard";
import {
  IconArrowBigDownFilled,
  IconExchange,
  IconRocket,
} from "@tabler/icons-react";
import DocsButton from "../articlesHelpers/DocsButton";
import SimpleImage from "../articlesHelpers/SimpleImage";

export const GET_INBOUND_CONTENT = (
  <TypographyStylesProvider>
    <p>
      Since the inception of the Lightning Network, new users have been faced
      with a puzzle when they start using a Lightning node.
    </p>

    <p>
      When your node first gets on the network, you have no inbound capacity.
      This means you can't receive payments from other nodes on the network.
    </p>
    <p>
      We've designed Rizful Node from the ground-up so we can provide an elegant
      and easy-to-use solution to this problem. You start the process with the
      "Get Inbound" button.
    </p>

    <DocsButton
      ariaLabel="deposit btc"
      text="Get Inbound"
      leftSection={<IconRocket />}
    />
    <p>You will then see a list of capacities and prices, like this:</p>

    <SimpleImage h={400} src="docs_images/channel-capacities-table-2.png" />

    <p>
      <strong>Channel Capacity</strong> tells you how big the channel will be.
      For a channel of 1,000,000 (one million) satoshis in size, you'll be able
      to receive about 1,000,000 (one million) satoshis.{" "}
    </p>
    <p>
      {" "}
      Note that payments can flow both ways in a channel. So you can receive and
      send, back and forth. Receiving funds on the channel will increase your
      outbound capacity, and sending funds with increase your inbound capacity.{" "}
    </p>
    <p>
      <strong>Cost</strong> is the size of the Lightning invoice we will ask you
      to pay to open the channel. This helps us cover the costs of the capital
      we need to provide the channel.
    </p>
    <p>
      {" "}
      Note that these <strong>cost</strong> values will change frequently, but
      you'll never pay more than the price quoted each time you open a channel.
      The cost changes because opening a lightning channel to your node requires
      that we initiate an on-chain transaction, and fees for on-chain
      transactions are always changing.
    </p>
    <p>
      <strong>Private Channel</strong> is checked by default, because for most
      users that will be the right choice. Private channels can't be publicly
      seen, and therefore enhance your privacy on the network. You should only
      open a <strong>Public Channel</strong> if you are sure that you need to be
      able to receive "keysend" payments, which are a legacy payment format on
      the network, where a node can be paid using only its public key.
    </p>

    <p>
      {" "}
      Note that if you uncheck <strong> Private Channel</strong>, and therefore
      request a <strong> Public Channel </strong> instead, be aware that your
      channel will require six (6) confirmations to be usable, which will take
      about an hour or so. If you leave the box checked, and therefore open a{" "}
      <strong> Private Channel </strong>, then your channel will be usable
      immediately.{" "}
    </p>
    <p>
      {" "}
      After choosing the size of your channel, you'll see an invoice like this:
    </p>

    <SimpleImage h={400} src="docs_images/get-inbound-channel-invoice.png" />

    <p>
      You can pay this invoice with any lightning-enabled Bitcoin wallet. If
      your wallet is on your phone, you can scan the QR code, or if you access
      it through a web browser, you can copy the invoice text with the copy
      button and paste it in your your browser.
    </p>

    <SimpleImage h={100} src="docs_images/channel-opened.png" />

    <p>
      Immediately after paying the invoice, you should see a channel like this
      appear on your node screen. Note that, although in this case I asked for a
      channel of 500,000 satoshis in size, I ended up with 491,530 satoshis of
      inbound capacity.
    </p>

    <p>
      In fact, if I were to come back to this page in a few hours, I might find
      that the amount of inbound capacity has shifted slightly -- I might find
      it at 491,200 satoshis or 491,800 satoshis. This can be confusing for new
      lightning users but is easily explained: On the Lightning Network each
      side of the channel must keep a "reserve", which is a small amount of
      satoshis that can't be spent.
    </p>
    <p>
      This reserve is used to pay the fees for closing the channel, and to
      ensure that the channel can't be closed with a zero balance. The reserve
      is a small percentage of the total channel size, and it is calculated
      based on the size of the channel and the current on-chain fee rates of the
      Bitcoin network. So, the amount of inbound liquidity you have will always
      be slightly less than the size of the channel you opened, and will be
      constantly changing depending on the prevailing fees on the Bitcoin
      network. Get it?
    </p>
  </TypographyStylesProvider>
);
